import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import RichTextEditor from '../../components/cms/RichTextEditor'
import { blogService } from '../../services/blogService'

function slugify(text) {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '')
}

const EMPTY = {
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    cover_image_url: '',
    status: 'draft',
}

export default function BlogEditorPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const isNew = !id || id === 'new'

    const [form, setForm] = useState(EMPTY)
    const [slugManuallyEdited, setSlugManuallyEdited] = useState(false)
    const [loading, setLoading] = useState(!isNew)
    const [saving, setSaving] = useState(false)
    const [saved, setSaved] = useState(false)
    const [error, setError] = useState(null)

    // Cover image upload
    const [coverUploading, setCoverUploading] = useState(false)
    const [coverError, setCoverError] = useState(null)
    const coverInputRef = useRef(null)

    // Document upload
    const [docUploading, setDocUploading] = useState(false)
    const [docError, setDocError] = useState(null)
    const docInputRef = useRef(null)

    useEffect(() => {
        if (isNew) return
        blogService.adminGet(id)
            .then((res) => {
                const post = res.data
                setForm({
                    title: post.title || '',
                    slug: post.slug || '',
                    excerpt: post.excerpt || '',
                    content: post.content || '',
                    cover_image_url: post.cover_image_url || '',
                    status: post.status || 'draft',
                })
                setSlugManuallyEdited(true) // existing posts: don't auto-regenerate slug
            })
            .catch((err) => setError(err.message || 'Failed to load post'))
            .finally(() => setLoading(false))
    }, [id, isNew])

    const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }))

    const handleTitleChange = (value) => {
        setField('title', value)
        if (!slugManuallyEdited) {
            setField('slug', slugify(value))
        }
    }

    const handleSlugChange = (value) => {
        setSlugManuallyEdited(true)
        setField('slug', slugify(value))
    }

    const handleSave = async (statusOverride) => {
        setError(null)
        setSaving(true)
        try {
            const payload = { ...form }
            if (statusOverride) payload.status = statusOverride

            let result
            if (isNew) {
                result = await blogService.create(payload)
                navigate(`/cms/blogs/${result.data.id}`, { replace: true })
            } else {
                result = await blogService.update(id, payload)
                setForm((f) => ({ ...f, status: result.data.status, slug: result.data.slug }))
            }
            setSaved(true)
            setTimeout(() => setSaved(false), 3000)
        } catch (err) {
            setError(err.message || 'Save failed')
        } finally {
            setSaving(false)
        }
    }

    const handleCoverImageUpload = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        setCoverError(null)
        setCoverUploading(true)
        try {
            const res = await blogService.uploadCoverImage(file)
            setField('cover_image_url', res.data.url)
        } catch (err) {
            setCoverError(err.message || 'Image upload failed')
        } finally {
            setCoverUploading(false)
            if (coverInputRef.current) coverInputRef.current.value = ''
        }
    }

    const handleDocumentUpload = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        setDocError(null)
        setDocUploading(true)
        try {
            const res = await blogService.parseDocument(file)
            setField('content', res.data.html)
        } catch (err) {
            setDocError(err.message || 'Document parse failed')
        } finally {
            setDocUploading(false)
            if (docInputRef.current) docInputRef.current.value = ''
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <SpinnerIcon className="w-6 h-6 animate-spin text-brand-navy" />
            </div>
        )
    }

    const isDraft = form.status === 'draft'

    return (
        <div className="space-y-6 max-w-4xl">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <Link to="/cms/blogs" className="text-xs text-admin-muted hover:text-admin-text transition-colors">Insights</Link>
                        <span className="text-admin-muted/50 text-xs">/</span>
                        <span className="text-xs text-admin-text font-medium">{isNew ? 'New Post' : 'Edit Post'}</span>
                    </div>
                    <h2 className="text-lg font-bold text-admin-heading">
                        {isNew ? 'New Blog Post' : 'Edit Blog Post'}
                    </h2>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                    {error && <span className="text-xs text-red-600 font-medium">{error}</span>}
                    {saved && !error && (
                        <span className="text-xs text-green-600 font-bold flex items-center gap-1">
                            <CheckIcon className="w-3.5 h-3.5" /> Saved
                        </span>
                    )}
                    <button
                        onClick={() => handleSave()}
                        disabled={saving}
                        className="px-4 py-2 text-sm font-bold bg-white border border-admin-border text-admin-text rounded hover:bg-slate-100 transition-all disabled:opacity-50 flex items-center gap-1.5"
                    >
                        {saving ? <SpinnerIcon className="w-4 h-4 animate-spin" /> : null}
                        Save Draft
                    </button>
                    {isDraft ? (
                        <button
                            onClick={() => handleSave('published')}
                            disabled={saving}
                            className="px-4 py-2 text-sm font-bold bg-brand-navy text-white rounded hover:bg-brand-navy-dark transition-all disabled:opacity-50 flex items-center gap-1.5"
                        >
                            Publish
                        </button>
                    ) : (
                        <button
                            onClick={() => handleSave('draft')}
                            disabled={saving}
                            className="px-4 py-2 text-sm font-bold bg-amber-500 text-white rounded hover:bg-amber-600 transition-all disabled:opacity-50"
                        >
                            Unpublish
                        </button>
                    )}
                </div>
            </div>

            {/* Status badge */}
            <div className="flex items-center gap-2">
                {form.status === 'published' ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold bg-green-50 text-green-700 border border-green-200 rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Published
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> Draft
                    </span>
                )}
            </div>

            {/* Meta fields */}
            <div className="bg-white border border-admin-border rounded-lg shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-admin-border bg-slate-50 flex items-center justify-between gap-4">
                    <h3 className="text-sm font-bold text-admin-heading">Post Details</h3>
                    <button
                        onClick={() => handleSave()}
                        disabled={saving}
                        className="px-3 py-1.5 text-xs font-bold bg-brand-navy text-white rounded hover:bg-brand-navy-dark transition-all disabled:opacity-50 flex items-center gap-1.5"
                    >
                        {saving ? <SpinnerIcon className="w-3.5 h-3.5 animate-spin" /> : null}
                        Save
                    </button>
                </div>
                <div className="p-6 space-y-5">
                    <Field label="Title *">
                        <input
                            type="text"
                            value={form.title}
                            onChange={(e) => handleTitleChange(e.target.value)}
                            placeholder="Enter post title…"
                            className="w-full px-3 py-2 text-sm bg-white border border-admin-border rounded-md text-admin-text placeholder-admin-muted focus:outline-none focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy/40 transition-all"
                        />
                    </Field>

                    <Field label="Slug" hint="Auto-generated from title. Edit to customise the URL.">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-admin-muted font-mono flex-shrink-0">/blog/</span>
                            <input
                                type="text"
                                value={form.slug}
                                onChange={(e) => handleSlugChange(e.target.value)}
                                placeholder="post-slug"
                                className="flex-1 px-3 py-2 text-sm bg-white border border-admin-border rounded-md text-admin-text font-mono placeholder-admin-muted focus:outline-none focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy/40 transition-all"
                            />
                        </div>
                    </Field>

                    <Field label="Excerpt" hint="Short summary shown on the blog listing page.">
                        <textarea
                            value={form.excerpt}
                            onChange={(e) => setField('excerpt', e.target.value)}
                            rows={3}
                            placeholder="Brief description of the post…"
                            className="w-full px-3 py-2 text-sm bg-white border border-admin-border rounded-md text-admin-text placeholder-admin-muted focus:outline-none focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy/40 transition-all resize-y"
                        />
                    </Field>

                    <Field label="Cover Image" hint="Upload a photo or paste a URL.">
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={form.cover_image_url}
                                onChange={(e) => setField('cover_image_url', e.target.value)}
                                placeholder="https://…"
                                className="flex-1 px-3 py-2 text-sm bg-white border border-admin-border rounded-md text-admin-text placeholder-admin-muted focus:outline-none focus:ring-2 focus:ring-brand-navy/20 focus:border-brand-navy/40 transition-all"
                            />
                            <input
                                ref={coverInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleCoverImageUpload}
                                className="hidden"
                                id="cover-image-upload"
                            />
                            <label
                                htmlFor="cover-image-upload"
                                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-bold border border-admin-border rounded cursor-pointer transition-all flex-shrink-0 ${
                                    coverUploading
                                        ? 'bg-slate-100 text-admin-muted cursor-not-allowed'
                                        : 'bg-white text-admin-text hover:bg-slate-100'
                                }`}
                            >
                                {coverUploading ? <SpinnerIcon className="w-4 h-4 animate-spin" /> : <PhotoIcon className="w-4 h-4" />}
                                {coverUploading ? 'Uploading…' : 'Upload Photo'}
                            </label>
                        </div>
                        {coverError && <p className="text-xs text-red-600">{coverError}</p>}
                        {form.cover_image_url && (
                            <img src={form.cover_image_url} alt="Cover preview" className="mt-2 h-32 object-cover rounded border border-admin-border" />
                        )}
                    </Field>
                </div>
            </div>

            {/* Content editor */}
            <div className="bg-white border border-admin-border rounded-lg shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-admin-border bg-slate-50 flex items-center justify-between gap-4">
                    <div>
                        <h3 className="text-sm font-bold text-admin-heading">Content</h3>
                        <p className="text-xs text-admin-muted mt-0.5">Write or import content. Uploading a document will replace the current content.</p>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                        {docError && <span className="text-xs text-red-600">{docError}</span>}
                        <input
                            ref={docInputRef}
                            type="file"
                            accept=".docx,.pdf,.txt"
                            onChange={handleDocumentUpload}
                            className="hidden"
                            id="doc-upload"
                        />
                        <label
                            htmlFor="doc-upload"
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border border-admin-border rounded cursor-pointer transition-all ${
                                docUploading
                                    ? 'bg-slate-100 text-admin-muted cursor-not-allowed'
                                    : 'bg-white text-admin-text hover:bg-slate-100'
                            }`}
                        >
                            {docUploading ? <SpinnerIcon className="w-3.5 h-3.5 animate-spin" /> : <UploadIcon className="w-3.5 h-3.5" />}
                            {docUploading ? 'Parsing…' : 'Import Document'}
                        </label>
                        <span className="text-[10px] text-admin-muted">.docx · .pdf · .txt</span>
                        <button
                            onClick={() => handleSave()}
                            disabled={saving}
                            className="px-3 py-1.5 text-xs font-bold bg-brand-navy text-white rounded hover:bg-brand-navy-dark transition-all disabled:opacity-50 flex items-center gap-1.5"
                        >
                            {saving ? <SpinnerIcon className="w-3.5 h-3.5 animate-spin" /> : null}
                            Save
                        </button>
                    </div>
                </div>

                <div className="p-4">
                    <RichTextEditor value={form.content} onChange={(html) => setField('content', html)} />
                </div>
            </div>

            {/* Bottom save bar */}
            <div className="flex items-center justify-between py-4 border-t border-admin-border">
                <Link to="/cms/blogs" className="text-sm text-admin-muted hover:text-admin-text transition-colors">
                    ← Back to all posts
                </Link>
                <div className="flex items-center gap-2">
                    {error && <span className="text-xs text-red-600 font-medium">{error}</span>}
                    <button
                        onClick={() => handleSave()}
                        disabled={saving}
                        className="px-4 py-2 text-sm font-bold bg-white border border-admin-border text-admin-text rounded hover:bg-slate-100 transition-all disabled:opacity-50"
                    >
                        Save Draft
                    </button>
                    {isDraft ? (
                        <button
                            onClick={() => handleSave('published')}
                            disabled={saving}
                            className="px-4 py-2 text-sm font-bold bg-brand-navy text-white rounded hover:bg-brand-navy-dark transition-all disabled:opacity-50"
                        >
                            {saving ? 'Publishing…' : 'Publish'}
                        </button>
                    ) : (
                        <button
                            onClick={() => handleSave('draft')}
                            disabled={saving}
                            className="px-4 py-2 text-sm font-bold bg-amber-500 text-white rounded hover:bg-amber-600 transition-all disabled:opacity-50"
                        >
                            Unpublish
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

function Field({ label, hint, children }) {
    return (
        <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-admin-muted uppercase tracking-wider">{label}</label>
            {children}
            {hint && <p className="text-[11px] text-admin-muted">{hint}</p>}
        </div>
    )
}

function CheckIcon({ className }) {
    return <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
}
function SpinnerIcon({ className }) {
    return <svg className={className} fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
}
function UploadIcon({ className }) {
    return <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
}
function PhotoIcon({ className }) {
    return <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
}
